import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface Brand {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  contact_info: any;
  terms_text: string;
}

interface BrandContextType {
  brands: Brand[];
  selectedBrand: Brand | null;
  currentBrand: Brand | null;
  selectedBrandIds: string[];
  setSelectedBrand: (brand: Brand | null) => void;
  setSelectedBrandIds: (brandIds: string[]) => void;
  loading: boolean;
  refreshBrands: () => Promise<void>;
  triggerDataRefresh: () => void;
  dataRefreshKey: number;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const { user, profile } = useAuth();

  useEffect(() => {
    if (user && profile) {
      loadBrands();
    } else {
      setBrands([]);
      setSelectedBrand(null);
      setSelectedBrandIds([]);
      setLoading(false);
    }
  }, [user, profile]);

  useEffect(() => {
    if (selectedBrand) {
      setSelectedBrandIds([selectedBrand.id]);
    }
  }, [selectedBrand]);

  async function loadBrands() {
    try {
      setLoading(true);

      if (profile?.role === 'admin') {
        const { data, error } = await supabase
          .from('brands')
          .select('*')
          .order('name');

        if (error) throw error;
        setBrands(data || []);
        if (data && data.length > 0 && !selectedBrand) {
          setSelectedBrand(data[0]);
        }
      } else {
        const { data: userBrands, error: userBrandsError } = await supabase
          .from('user_brands')
          .select('brand_id')
          .eq('user_id', user!.id);

        if (userBrandsError) throw userBrandsError;

        if (userBrands && userBrands.length > 0) {
          const brandIds = userBrands.map(ub => ub.brand_id);
          const { data, error } = await supabase
            .from('brands')
            .select('*')
            .in('id', brandIds)
            .order('name');

          if (error) throw error;
          setBrands(data || []);
          if (data && data.length > 0 && !selectedBrand) {
            setSelectedBrand(data[0]);
          }
        } else {
          setBrands([]);
        }
      }
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setLoading(false);
    }
  }

  async function refreshBrands() {
    await loadBrands();
  }

  function triggerDataRefresh() {
    setDataRefreshKey(prev => prev + 1);
  }

  const value = {
    brands,
    selectedBrand,
    currentBrand: selectedBrand,
    selectedBrandIds,
    setSelectedBrand,
    setSelectedBrandIds,
    loading,
    refreshBrands,
    triggerDataRefresh,
    dataRefreshKey,
  };

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}
